import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './SpaceShooter.css';

const SpaceShooter = () => {
  const [playerX, setPlayerX] = useState(175);
  const [bullets, setBullets] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [keys, setKeys] = useState({});
  const [canShoot, setCanShoot] = useState(true);
  const animationRef = useRef(null);

  const GAME_WIDTH = 400;
  const GAME_HEIGHT = 500;
  const PLAYER_SIZE = 40;
  const PLAYER_SPEED = 5;

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setPlayerX(175);
    setBullets([]);
    setEnemies([]);
    setScore(0);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (['a', 'd', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
        setKeys(k => ({ ...k, [key]: true }));
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      setKeys(k => ({ ...k, [key]: false }));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const spawnEnemy = setInterval(() => {
      setEnemies(e => [...e, {
        x: Math.random() * (GAME_WIDTH - 30),
        y: -30,
        id: Date.now()
      }]);
    }, 1500);

    return () => clearInterval(spawnEnemy);
  }, [gameActive, gameOver]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const handleShoot = (e) => {
      const key = e.key.toLowerCase();
      if (key === ' ' && canShoot) {
        e.preventDefault();
        setBullets(b => [...b, { x: playerX + 20, y: 440, id: Date.now() + Math.random() }]);
        setCanShoot(false);
        setTimeout(() => setCanShoot(true), 200);
      }
    };

    window.addEventListener('keydown', handleShoot);
    return () => window.removeEventListener('keydown', handleShoot);
  }, [gameActive, gameOver, playerX, canShoot]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const gameLoop = () => {
      if (keys['a'] || keys['arrowleft']) {
        setPlayerX(x => Math.max(0, x - PLAYER_SPEED));
      }
      if (keys['d'] || keys['arrowright']) {
        setPlayerX(x => Math.min(GAME_WIDTH - PLAYER_SIZE, x + PLAYER_SPEED));
      }

      setBullets(b => b
        .map(bullet => ({ ...bullet, y: bullet.y - 8 }))
        .filter(bullet => bullet.y > 0)
      );

      setEnemies(e => {
        const newEnemies = e.map(enemy => ({ ...enemy, y: enemy.y + 2 }));
        
        newEnemies.forEach(enemy => {
          if (enemy.y + 30 > 450 && enemy.y < 490 &&
              enemy.x < playerX + PLAYER_SIZE && enemy.x + 30 > playerX) {
            setGameActive(false);
            setGameOver(true);
          }
        });

        return newEnemies.filter(enemy => enemy.y < GAME_HEIGHT);
      });

      setBullets(currentBullets => {
        let remainingBullets = [...currentBullets];
        setEnemies(currentEnemies => {
          const remainingEnemies = currentEnemies.filter(enemy => {
            let hit = false;
            for (let i = remainingBullets.length - 1; i >= 0; i--) {
              const bullet = remainingBullets[i];
              // Check if bullet overlaps with enemy
              if (bullet.x >= enemy.x - 5 && bullet.x <= enemy.x + 35 &&
                  bullet.y >= enemy.y - 5 && bullet.y <= enemy.y + 35) {
                hit = true;
                remainingBullets.splice(i, 1);
                setScore(s => s + 10);
                break;
              }
            }
            return !hit;
          });
          return remainingEnemies;
        });
        return remainingBullets;
      });

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationRef.current);
  }, [gameActive, gameOver, keys, playerX]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🚀 Space Shooter</h1>
        <div className="game-instructions">
          Use A/D or ← → to move. Press SPACE to shoot!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        <div className="game-play-area">
          <div className="space-game-area">
            <div className="player" style={{ left: playerX }}>▲</div>
            
            {bullets.map(bullet => (
              <div key={bullet.id} className="bullet" style={{ left: bullet.x, top: bullet.y }} />
            ))}
            
            {enemies.map(enemy => (
              <div key={enemy.id} className="enemy" style={{ left: enemy.x, top: enemy.y }}>👾</div>
            ))}

            {!gameActive && (
              <div className="game-status-overlay">
                {gameOver ? (
                  <div className="status-message lose">💥 Game Over!</div>
                ) : (
                  <div className="status-message start">Press Start to Play!</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default SpaceShooter;
